import { useContext, useEffect, useState } from 'react';
import '../App.css';

import {
    CaretSortIcon,
    CheckIcon,
    DotsHorizontalIcon,
    DownloadIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import { CSVLink } from "react-csv";
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { BunnyLogDatatable } from '../components/datatables/bunnyLogDatatable';
import { ClubsLogDatatable } from '../components/datatables/clubsLogDatatable';
import { CompetitionLogDatatable } from '../components/datatables/competitionLogDatatable';
import { SuperadminsLogDatatable } from '../components/datatables/superadminsLogDatatable';
import { DatePickerWithRange } from '../components/daterangeWithpicker';
import DeleteUserGDPRDataDialog from '../components/dialogs/deleteUserGDPRDataDialog';
import EnqeueAllBunniesFamilyTreesDialog from '../components/dialogs/enqueueAllBunniesFamilyTreesDialog';
import ExportJudgesClubsStatsDialog from '../components/dialogs/exportJudgesClubsStatsDialog';
import ExportJudgesStatsDialog from '../components/dialogs/exportJudgesStatsDialog';
import ExportUserGDPRDataDialog from '../components/dialogs/exportUserGDPRDataDialog';
import GenerateFamilyTreesDialog from '../components/dialogs/generateFamilyTreesDialog';
import { Button } from '../components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../components/ui/command";
import {
    Dialog,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../components/ui/popover";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';
import { cn } from '../lib/utils';

function Logs() {
    const [clubs, setClubs] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const [selectedCompetition, setSelectedCompetition] = useState();
    const [selectedDates, setSelectedDates] = useState({ from: null, to: null });
    const [isLoading, setIsLoading] = useState(false);
    const [logData, setLogData] = useState([]);
    const { auth } = useContext(AuthContext);
    const form = useForm();

    const [searchParams] = useSearchParams();
    const competitionId = searchParams.get('competitionId');
    const [comboboxOpen, setComboboxOpen] = useState(false);

    const [selectedTab, setSelectedTab] = useState();

    useEffect(() => {
        if (competitionId) {
            setSelectedTab("competitions"); // Automatically switch to the competitions tab
            setSelectedCompetition(competitionId); // Prefill the competition ID
            form.setValue("competitionId", competitionId); // Populate the input field
        }
    }, [competitionId, form]);

    useEffect(() => {
        setLogData([]); // Reset data
        setSelectedClub(null); // Reset selected club
        setSelectedDates({ from: null, to: null }); // Reset selected dates
        form.setValue("clubName", null)
    }, [selectedTab, form]);

    const handleSubmit = (data) => {
        setLogData([]); // Reset data

        switch (selectedTab) {
            case 'clubs':
                sendClubsGetRequest(data);
                break;
            case 'competitions':
                sendCompetitionsGetRequest(data);
                break;
            case 'bunnies':
                sendBunniesGetRequest(data);
                break;
            case 'admin':
                sendSuperadminsGetRequest(data);
                break;
            default:

                break;
        }
    };

    const handleTabChange = (tab) => {
        setSelectedTab(tab);
        if (tab === 'clubs') {
            fetchClubs();
        }
    };

    const fetchClubs = () => {
        setIsLoading(true); // Start loading
        api.get('https://skhrf-api.cmdigital.se/v1/clubs/')
            .then(response => {
                const allClubs = response.data;
                let filteredClubs = allClubs;

                if (auth.user?.roleInClub === 'admin' && !auth.user?.superadmin) {
                    filteredClubs = allClubs.filter(club => club.id === auth.user?.memberOfClub);
                }

                setClubs(filteredClubs);

                if (filteredClubs.length === 1) {
                    setSelectedClub(filteredClubs[0].id);
                    form.setValue("clubName", filteredClubs[0].id);
                }
            })
            .catch(error => {
                console.error('Error fetching clubs:', error);
                setLogData([]);
            })
            .finally(() => {
                setIsLoading(false); // Stop loading
            });
    };

    const sendSuperadminsGetRequest = async (e) => {
        if (isLoading) return; // If loading, return

        e.preventDefault();

        setIsLoading(true); // Set loading to true

        const convertToUTC = (date) => {
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };

        const fromDate = selectedDates.from ? convertToUTC(selectedDates.from) : 'DATUM';
        const toDate = selectedDates.to ? convertToUTC(selectedDates.to) : 'DATUM';

        api.get(`https://skhrf-api.cmdigital.se/v1/logs/superadmins?from=${fromDate}&to=${toDate}`)
            .then(response => {

                setLogData(response.data); // Set the fetched data
                setIsLoading(false); // Request successful, set loading to false
            })
            .catch(error => {
                setLogData([]);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false); // Error occurred, set loading to false
            });
    }

    const sendBunniesGetRequest = async (e) => {
        if (isLoading) return; // If loading, return

        e.preventDefault();

        setIsLoading(true); // Set loading to true

        const convertToUTC = (date) => {
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };

        const fromDate = selectedDates.from ? convertToUTC(selectedDates.from) : 'DATUM';
        const toDate = selectedDates.to ? convertToUTC(selectedDates.to) : 'DATUM';

        api.get(`https://skhrf-api.cmdigital.se/v1/logs/bunnies?from=${fromDate}&to=${toDate}`)
            .then(response => {

                setLogData(response.data); // Set the fetched data
                setIsLoading(false); // Request successful, set loading to false
            })
            .catch(error => {
                // console.error('Error getting data:' + error?.response?.data?.error); // Log
                setLogData([]);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false); // Error occurred, set loading to false
            });
    }

    const sendClubsGetRequest = async (e) => {
        if (isLoading) return; // If loading, return


        e.preventDefault();

        setIsLoading(true); // Set loading to true

        if (selectedClub === null) {
            toast({
                variant: 'destructive',
                title: 'Ingen förening vald',
                description: 'Var god välj en förening för att se loggar.',
            });
            setIsLoading(false);
            return;
        }

        const convertToUTC = (date) => {
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };

        const fromDate = selectedDates.from ? convertToUTC(selectedDates.from) : 'DATUM';
        const toDate = selectedDates.to ? convertToUTC(selectedDates.to) : 'DATUM';

        api.get(`https://skhrf-api.cmdigital.se/v1/logs/clubs?clubId=${selectedClub}&from=${fromDate}&to=${toDate}`)
            .then(response => {

                setLogData(response.data); // Set the fetched data
                setIsLoading(false); // Request successful, set loading to false
            })
            .catch(error => {
                // console.error('Error getting data:' + error?.response?.data?.error); // Log
                setLogData([]);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false); // Error occurred, set loading to false
            });
    }

    const sendCompetitionsGetRequest = async (e) => {
        if (isLoading) return; // If loading, return

        e.preventDefault();

        setIsLoading(true); // Set loading to true

        const convertToUTC = (date) => {
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };

        const fromDate = selectedDates.from ? convertToUTC(selectedDates.from) : 'DATUM';
        const toDate = selectedDates.to ? convertToUTC(selectedDates.to) : 'DATUM';

        if (selectedCompetition == null) {
            toast({
                variant: 'destructive',
                title: 'Ingen tävling angiven',
                description: 'Var god ange tävlings-id för att se loggar.',
            });
            setIsLoading(false);
            return;
        }

        if (!fromDate || !toDate) {
            toast({
                variant: 'destructive',
                title: 'Inget datum angivet.',
                description: 'Var god ange start- och slutdatum för att se loggar.',
            });
            setIsLoading(false);
            return;
        }

        api.get(`https://skhrf-api.cmdigital.se/v1/logs/competitions?from=${fromDate}&to=${toDate}&competitionId=${selectedCompetition}`)
            .then(response => {

                setLogData(response.data); // Set the fetched data
                setIsLoading(false); // Request successful, set loading to false
            })
            .catch(error => {
                // console.error('Error getting data:' + error?.response?.data?.error); // Log
                setLogData([]);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false); // Error occurred, set loading to false
            });
    }

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl'>System</h1>
                <p className='pb-2 text-muted-foreground text-sm'>Här kan du se händelser som loggas i systemet, välj respektive tabb för mer information.</p>
                <Tabs value={selectedTab} onValueChange={handleTabChange} className="flex flex-col">
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
                        <TabsList className='flex flex-wrap justify-start h-fit w-fit'>
                            {auth.user?.superadmin && (
                                <>
                                    <TabsTrigger value="clubs">Föreningsloggar</TabsTrigger>
                                    <TabsTrigger value="competitions">Tävlingsloggar</TabsTrigger>
                                    <TabsTrigger value="bunnies">Kaninloggar</TabsTrigger>
                                    <TabsTrigger value="admin">Förbundsloggar</TabsTrigger>
                                </>
                            )}
                            {(!auth.user?.superadmin && auth.user?.roleInClub === 'admin') && (
                                <>
                                    <TabsTrigger value="clubs">Föreningsloggar</TabsTrigger>
                                    <TabsTrigger value="competitions">Tävlingsloggar</TabsTrigger>
                                </>
                            )}
                            {(!auth.user?.superadmin && auth?.user?.bunnyadmin) && (
                                <>
                                    <TabsTrigger value="bunnies">Kaninloggar</TabsTrigger>
                                </>
                            )}
                        </TabsList>

                        {(auth?.user?.superadmin || auth?.user?.bunnyadmin || auth.user?.roleInClub === 'admin') &&
                            <Dialog modal={true}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild >
                                        <Button variant='secondary'><DotsHorizontalIcon className='mr-3' />Verktyg</Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className='dropdown-content-width-full'>
                                        <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                        <DropdownMenuSeparator />

                                        {(auth.user?.superadmin || auth.user?.roleInClub === 'admin') && (
                                            <ExportJudgesClubsStatsDialog dropdown={true} />
                                        )}

                                        {auth?.user?.superadmin &&
                                            <>
                                                <ExportJudgesStatsDialog dropdown={true} />
                                                <ExportUserGDPRDataDialog dropdown={true} />
                                                <DropdownMenuSeparator />
                                            </>
                                        }
                                        {(auth?.user?.superadmin || auth?.user?.bunnyadmin) &&
                                            <>
                                                <EnqeueAllBunniesFamilyTreesDialog dropdown={true} />
                                                <GenerateFamilyTreesDialog dropdown={true} />
                                            </>
                                        }
                                        {auth?.user?.superadmin &&
                                            <>
                                                <DropdownMenuSeparator />
                                                <DeleteUserGDPRDataDialog dropdown={true} />
                                            </>
                                        }
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </Dialog>
                        }
                    </div>
                    <TabsContent value="clubs">
                        <p className='text-muted-foreground text-sm'>Föreningsrelevanta loggar inkluderar nya medlemmar, uppdatering av utgångdatum för medlemskap samt flytt av medlem till och från annan förening.</p>
                        <Form {...form} >
                            <form onSubmit={handleSubmit} className="flex flex-col lg:items-end lg:flex-row gap-3 w-full py-6">
                                <FormField control={form.control}
                                    name='clubsDates'
                                    render={({ field }) =>
                                        <>
                                            <DatePickerWithRange className={'w-full md:w-96'} disabled={isLoading} id='clubsDates' onDateChange={setSelectedDates} />
                                        </>
                                    }
                                />
                                <FormField
                                    control={form.control}
                                    name="clubName"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <Popover modal={true} open={comboboxOpen} onOpenChange={setComboboxOpen}>
                                                <FormLabel>Förening</FormLabel>
                                                <PopoverTrigger asChild>
                                                    <FormControl>
                                                        <Button
                                                            variant="outline"
                                                            role="combobox"
                                                            disabled={isLoading /*|| clubs.length === 1*/} // Intended feature to autoselect the only club they have available.
                                                            className={cn(
                                                                "min-w-[200px] justify-between",
                                                                !field.value && "text-muted-foreground"
                                                            )}
                                                        >
                                                            {field.value
                                                                ? clubs.find(
                                                                    (club) => club.id === field.value
                                                                )?.name
                                                                : "Välj förening"}
                                                            <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                        </Button>
                                                    </FormControl>
                                                </PopoverTrigger>
                                                <PopoverContent className="popover-content-width-full p-0">
                                                    <Command>
                                                        <CommandInput placeholder="Sök förening..." />
                                                        <CommandList>
                                                            <CommandEmpty>Ingen förening hittades.</CommandEmpty>
                                                            <CommandGroup>
                                                                {clubs.map((club) => (
                                                                    <CommandItem
                                                                        className='cursor-pointer'
                                                                        value={club.id}
                                                                        key={club.id}
                                                                        onSelect={() => {
                                                                            form.setValue("clubName", club.id)
                                                                            setSelectedClub(club.id);
                                                                            setComboboxOpen(false)
                                                                        }}
                                                                    >
                                                                        <CheckIcon
                                                                            className={cn(
                                                                                "mr-2 h-4 w-4",
                                                                                club.id === field.value
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0"
                                                                            )}
                                                                        />
                                                                        {club.name}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Button type="submit" className="w-full lg:w-fit min-w-24" disabled={isLoading}>
                                    {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Sök </>}
                                </Button>
                                {logData.length > 0 && (
                                    <Button variant='secondary' className="w-full lg:w-fit">
                                        <CSVLink filename={"club_" + selectedClub + "_logs.csv"} data={logData.map(logline => ({
                                            "Tidpunkt": logline.created_at,
                                            "Utövare": (logline.performer?.first_name + ' ' + logline.performer?.last_name),
                                            "Användare": (logline.targetUser?.first_name + ' ' + logline.targetUser?.last_name),
                                            "Beskrivning": logline.description
                                        }))} className="flex items-center justify-center w-full min-w-36">
                                            <DownloadIcon className='mr-3' />
                                            Ladda ner
                                        </CSVLink>
                                    </Button>
                                )}
                            </form>
                        </Form>
                        <ClubsLogDatatable data={logData} isLoading={isLoading} />
                    </TabsContent>
                    <TabsContent value="competitions">
                        <p className='text-muted-foreground text-sm'>Tävlingsrelevanta loggar inkluderar skapade tävlingar, ändrade tävlingar, anmälningar och borttagna tävlingar.</p>
                        <Form {...form} >
                            <form onSubmit={handleSubmit} className="flex flex-col lg:items-end lg:flex-row gap-3 w-full py-6">
                                <FormField control={form.control}
                                    name='competitionDates'
                                    render={({ field }) =>
                                        <>
                                            <DatePickerWithRange className={'w-full md:w-96'} disabled={isLoading} id='competitionDates' onDateChange={setSelectedDates} />
                                        </>
                                    }
                                />
                                <FormField
                                    control={form.control}
                                    name="competitionId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className='flex flex-col gap-2'>
                                                    <Label htmlFor='selectedCompetition'>Tävlings-ID</Label>
                                                    <Input {...field} type="number" disabled={isLoading} id='selectedCompetition' placeholder="Tävlings-ID" onChangeCapture={e => setSelectedCompetition(e.currentTarget.value)} />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <Button type="submit" className="w-full lg:w-fit min-w-24" disabled={isLoading}>
                                    {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Sök </>}
                                </Button>
                                {logData.length > 0 && (
                                    <Button variant='secondary' className="w-full lg:w-fit">
                                        <CSVLink filename={`competition_${selectedCompetition}_logs.csv`} data={logData.map(logline => ({
                                            "Tidpunkt": logline.created_at,
                                            "Utövare": (logline.performer?.first_name + ' ' + logline.performer?.last_name),
                                            "Beskrivning": logline.description
                                        }))} className="flex items-center justify-center w-full min-w-36">
                                            <DownloadIcon className='mr-3' />
                                            Ladda ner
                                        </CSVLink>
                                    </Button>
                                )}
                            </form>
                        </Form>
                        <CompetitionLogDatatable data={logData} isLoading={isLoading} />
                    </TabsContent>
                    <TabsContent value="bunnies">
                        <p className='text-muted-foreground text-sm'>Kaninloggar inkluderar skapade kaniner, godkända kaniner, kaniner markerade som avlidna och nekade kaniner.</p>
                        <Form {...form} >
                            <form onSubmit={handleSubmit} className="flex flex-col lg:items-end lg:flex-row gap-3 w-full py-6">
                                <FormField control={form.control}
                                    name='bunnyDates'
                                    render={({ field }) =>
                                        <>
                                            <DatePickerWithRange className={'w-full md:w-96'} disabled={isLoading} id='bunnyDates' onDateChange={setSelectedDates} />
                                        </>
                                    }
                                />
                                <Button type="submit" className="ww-full lg:w-fit min-w-24" disabled={isLoading}>
                                    {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Sök </>}
                                </Button>
                                {logData.length > 0 && (
                                    <Button variant='secondary' className="w-full lg:w-fit">
                                        <CSVLink filename={"bunny_logs.csv"} data={logData.map(logline => ({
                                            "Tidpunkt": logline.created_at,
                                            "Utövare": (logline.performer?.first_name + ' ' + logline.performer?.last_name),
                                            "Beskrivning": logline.description
                                        }))} className="flex items-center justify-center w-full min-w-36">
                                            <DownloadIcon className='mr-3' />
                                            Ladda ner
                                        </CSVLink>
                                    </Button>
                                )}
                            </form>
                        </Form>

                        <BunnyLogDatatable data={logData} isLoading={isLoading} />
                    </TabsContent>
                    <TabsContent value="admin">
                        <p className='text-muted-foreground text-sm'>Förbundsloggar inkluderar skapade och arkiverade föreningar, befodran till och från kaninadministratör samt befodran till och från domare.</p>
                        <Form {...form} >
                            <form onSubmit={handleSubmit} className="flex flex-col lg:items-end lg:flex-row gap-3 w-full py-6">
                                <FormField control={form.control}
                                    name='adminDates'
                                    render={({ field }) =>
                                        <>
                                            <DatePickerWithRange className={'w-full md:w-96'} disabled={isLoading} id='adminDates' onDateChange={setSelectedDates} />
                                        </>
                                    }
                                />
                                <Button type="submit" className="w-full lg:w-fit min-w-24" disabled={isLoading}>
                                    {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Sök </>}
                                </Button>
                                {logData.length > 0 && (
                                    <Button variant='secondary' className="w-full lg:w-fit">
                                        <CSVLink filename={"admin_logs.csv"} data={logData.map(logline => ({
                                            "Tidpunkt": logline.created_at,
                                            "Utövare": (logline.performer?.first_name + ' ' + logline.performer?.last_name),
                                            "Användare": (logline.targetUser?.first_name + ' ' + logline.targetUser?.last_name),
                                            "Beskrivning": logline.description
                                        }))} className="flex items-center justify-center w-full min-w-36">
                                            <DownloadIcon className='mr-3' />
                                            Ladda ner
                                        </CSVLink>
                                    </Button>
                                )}
                            </form>
                        </Form>

                        <SuperadminsLogDatatable data={logData} isLoading={isLoading} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

export default Logs;
